import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { contactsAPI, adminAPI } from "../../services/api";
import "./ContactDetail.css";
import { useToast } from "../../hooks/useToast";

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  department: string;
  message: string;
  status: "open" | "closed";
  assignedTo?: { _id: string; name: string; email: string } | string;
  notes?: string;
  createdAt: string;
}

interface Admin {
  _id: string;
  name: string;
  email: string;
}

const ContactDetail = () => {
  const { showToast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    status: "open" as "open" | "closed",
    assignedTo: "",
    notes: "",
  });

  useEffect(() => {
    fetchContactAndAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContactAndAdmins = async () => {
    try {
      setIsLoading(true);
      const [contactRes, adminsRes] = await Promise.all([
        contactsAPI.getById(id!),
        adminAPI.getAllAdmins(),
      ]);

      const contactData = contactRes.data.data || contactRes.data;
      setContact(contactData);

      const adminsData = adminsRes.data.data || adminsRes.data || [];
      setAdmins(Array.isArray(adminsData) ? adminsData : []);

      const assignedId =
        typeof contactData.assignedTo === "object"
          ? contactData.assignedTo?._id
          : contactData.assignedTo;

      setFormData({
        status: contactData.status || "open",
        assignedTo: assignedId || "",
        notes: contactData.notes || "",
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      showToast({
        type:"error",
        title:"Error",
        message:
        "Failed to load contact details"});
      navigate("/admin/contacts");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!contact) return;

    try {
      setIsSaving(true);
      await contactsAPI.update(contact._id, formData);
      showToast({
        type:"success",
        title:"Contact updated",
        message:
        "Contact updated successfully"});
      await fetchContactAndAdmins();
    } catch (error) {
      console.error("Failed to update contact:", error);
      showToast({
        type:"error",
        title:"Update failed",
        message:  
        "Failed to update contact"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    if (!window.confirm("Are you sure you want to delete this contact?"))
      return;

    try {
      await contactsAPI.delete(contact._id);
      showToast({
        type:"success",
        title:"Contact deleted",
        message:
        "Contact deleted successfully"});
      navigate("/admin/contacts");
    } catch (error) {
      console.error("Failed to delete contact:", error);
      showToast({
        type:"error",
        title:"Delete failed",
        message:
        "Failed to delete contact"});
    }
  };

  if (isLoading) {
    return (
      <div className="contact-detail-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading contact details...</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="contact-detail-page">
        <div className="loading-state">
          <p>Contact not found</p>
          <button
            onClick={() => navigate("/admin/contacts")}
            className="back-button"
          >
            ← Back to Contacts
          </button>
        </div>
      </div>
    );
  }

  const getAssignedName = () => {
    if (!contact.assignedTo) return "Unassigned";
    if (typeof contact.assignedTo === "object") {
      return `${contact.assignedTo.name} (${contact.assignedTo.email})`;
    }
    return contact.assignedTo;
  };

  return (
    <div className="contact-detail-page">
      <div className="detail-header">
        <button
          onClick={() => navigate("/admin/contacts")}
          className="back-button"
        >
          <span>←</span>
          <span>Back to Contacts</span>
        </button>
      </div>

      <div className="detail-content">
        <div className="detail-card">
          <h2 className="card-title">
            <span className="title-icon">📧</span>
            Contact Information
          </h2>

          <div className="info-grid">
            <div className="info-item">
              <label className="info-label">Name</label>
              <div className="info-value">{contact.name}</div>
            </div>

            <div className="info-item">
              <label className="info-label">Email</label>
              <div className="info-value email">{contact.email}</div>
            </div>

            {contact.phone && (
              <div className="info-item">
                <label className="info-label">Phone</label>
                <div className="info-value phone">{contact.phone}</div>
              </div>
            )}

            <div className="info-item">
              <label className="info-label">Subject</label>
              <div className="info-value">{contact.subject}</div>
            </div>

            <div className="info-item">
              <label className="info-label">Department</label>
              <div
                className="info-value"
                style={{ textTransform: "capitalize" }}
              >
                {contact.department}
              </div>
            </div>

            <div className="info-item">
              <label className="info-label">Status</label>
              <div>
                <span className={`status-badge-large ${contact.status}`}>
                  {contact.status}
                </span>
              </div>
            </div>

            <div className="info-item">
              <label className="info-label">Assigned To</label>
              <div className="info-value">{getAssignedName()}</div>
            </div>

            <div className="info-item">
              <label className="info-label">Received</label>
              <div className="date-info">
                <span className="date-icon">📅</span>
                <span>{new Date(contact.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <h3 className="card-title">
              <span className="title-icon">💬</span>
              Message
            </h3>
            <div className="message-box">{contact.message}</div>
          </div>

          {contact.notes && (
            <div style={{ marginTop: "1.5rem" }}>
              <h3 className="card-title">
                <span className="title-icon">📝</span>
                Internal Notes
              </h3>
              <div className="message-box">{contact.notes}</div>
            </div>
          )}
        </div>

        <div className="detail-card actions-card">
          <h2 className="card-title">
            <span className="title-icon">⚙️</span>
            Actions
          </h2>

          <div className="action-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as "open" | "closed",
                })
              }
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div className="action-group">
            <label htmlFor="assignedTo">Assign To</label>
            <select
              id="assignedTo"
              value={formData.assignedTo}
              onChange={(e) =>
                setFormData({ ...formData, assignedTo: e.target.value })
              }
            >
              <option value="">Unassigned</option>
              {admins.map((admin) => (
                <option key={admin._id} value={admin._id}>
                  {admin.name} ({admin.email})
                </option>
              ))}
            </select>
          </div>

          <div className="action-group">
            <label htmlFor="notes">Internal Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Add internal notes about this contact..."
            />
          </div>

          <div className="action-buttons">
            <button
              onClick={handleUpdate}
              disabled={isSaving}
              className="action-btn primary"
            >
              {isSaving ? (
                <>
                  <div className="spinner-small"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>Save Changes</span>
                </>
              )}
            </button>

            <button onClick={handleDelete} className="action-btn danger">
              <span>🗑️</span>
              <span>Delete Contact</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetail;
